import { useCallback, useEffect, useState } from "react";
import { ShieldBan, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, errMsg } from "../../lib/api";
import { COUNTRY_CODES, formatMobile } from "../../lib/constants";

const blank = { code: "+91", mobile: "", name: "", reason: "" };

export const CriticalGuestsTab = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setList((await api.get("/critical-guests")).data); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/critical-guests", { mobile: `${f.code} ${f.mobile}`, name: f.name, reason: f.reason });
      toast.success("Number added to Critical guests");
      setF(blank);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Remove ${g.mobile} from Critical guests? Feedback can be taken from this number again.`)) return;
    try { await api.delete(`/critical-guests/${g.id}`); toast.success("Removed"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="glass rounded-3xl overflow-hidden" data-testid="critical-guests-tab">
      <div className="p-5">
        <div className="flex items-center gap-2"><ShieldBan className="h-5 w-5 text-red-600" /><h3 className="text-lg font-bold text-emerald-950">Critical guests</h3></div>
        <p className="text-xs text-slate-500 mt-1">Feedback can't be taken from these mobile numbers at any property. Staff see an error as soon as they type one.</p>
        <form onSubmit={add} className="mt-4 flex flex-wrap gap-2">
          <Select value={f.code} onValueChange={(v) => setF({ ...f, code: v, mobile: formatMobile(v, f.mobile) })}>
            <SelectTrigger data-testid="critical-code-select" className="h-10 w-[92px] rounded-full bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{COUNTRY_CODES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="critical-mobile-input" required inputMode="tel" value={f.mobile} onChange={(e) => setF({ ...f, mobile: formatMobile(f.code, e.target.value) })} placeholder="Mobile number" className="h-10 w-full sm:w-48 rounded-full bg-white" />
          <Input data-testid="critical-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Guest name (optional)" className="h-10 w-full sm:w-52 rounded-full bg-white" />
          <Input data-testid="critical-reason-input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Reason (optional)" className="h-10 w-full sm:w-64 rounded-full bg-white" />
          <button data-testid="critical-add-button" disabled={busy} className="h-10 px-4 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 flex items-center gap-2 transition-colors disabled:opacity-60"><Plus className="h-4 w-4" /> Add number</button>
        </form>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead><tr className="bg-emerald-950/[0.03] text-left">{["Mobile", "Name", "Reason", "Added by", "Added on", ""].map((h) => <th key={h} className="px-5 py-3 field-label">{h}</th>)}</tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No Critical guests yet.</td></tr>}
            {list.map((g) => (
              <tr key={g.id} data-testid={`critical-row-${g.mobile_digits}`} className="border-t border-emerald-900/5">
                <td className="px-5 py-3 font-semibold text-red-700">{g.mobile}</td>
                <td className="px-5 py-3">{g.name || "—"}</td>
                <td className="px-5 py-3 text-slate-600">{g.reason || "—"}</td>
                <td className="px-5 py-3 text-xs text-slate-600">{g.added_by}</td>
                <td className="px-5 py-3 text-xs text-slate-600">{new Date(g.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</td>
                <td className="px-5 py-3"><button data-testid={`critical-remove-${g.mobile_digits}`} onClick={() => remove(g)} className="h-8 w-8 grid place-items-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
